import { Avatar, AVATAR_SIZES } from "@neru/base/components";
import { config } from "@neru/base/config";
import {
  useCurrentAccount,
  useNavigateTo,
  useValidSession,
} from "@neru/base/hooks";
import { ProductType_Enum, STATUS_TO_CONNOTATION_MAP } from "@neru/base/types";
import { getAccountId, Text } from "@neru/base/utils";
import type { Connotations, WithChildren } from "@neru/types";
import {
  Badge,
  Card,
  Column,
  H3,
  Icon,
  type IconType,
  Menu,
  Phrase,
  Row,
  Tooltip,
} from "@neru/ui";

import type { GetProductsResponse } from "./hooks";

interface ProductCardProps {
  product: GetProductsResponse["data"]["Products"][number];
}

interface WrapperProps extends WithChildren {
  isLive: boolean;
  isPublic: boolean;
}

enum PRODUCT_ACTIONS {
  manageVersions = "MANAGE_VERSIONS",
}

const PRODUCT_OPTIONS: Array<{
  action: PRODUCT_ACTIONS;
  connotation: Connotations;
  icon: IconType;
  title: string;
}> = [
  {
    action: PRODUCT_ACTIONS.manageVersions,
    connotation: "primary",
    icon: "list-line",
    title: "Manage versions",
  },
];

const Wrapper = ({ children, isLive, isPublic }: WrapperProps) =>
  isLive && isPublic ? (
    <>{children}</>
  ) : (
    <Tooltip mouseEnterDelay={0.5} text="This product is only visible to you.">
      {children}
    </Tooltip>
  );

export const ProductCard = ({ product }: ProductCardProps) => {
  const { canCreateProducts } = useCurrentAccount();
  const navigateTo = useNavigateTo();

  const {
    icon,
    id,
    isLive,
    isPublic,
    LatestVersion,
    name,
    owner,
    owner_name,
    short_description,
    tags,
    type,
  } = product;

  const lastVersion = (LatestVersion ?? [])[0];
  const isProductOwner = getAccountId() === owner;

  const navigateToProductOverview = () => {
    navigateTo("/:id", { id, name });
  };

  const onMenuOptionSelected = async (action: PRODUCT_ACTIONS) => {
    switch (action) {
      case PRODUCT_ACTIONS.manageVersions:
        navigateTo("/:id/versions", { id, name });
    }
  };

  const getProductTypeIcon = () => {
    if (type === ProductType_Enum.Private)
      return (
        <Tooltip placement="bottom" text="Private product">
          <Icon connotation="info" size="small" type="lock-solid" />
        </Tooltip>
      );

    if (type === ProductType_Enum.Unlisted)
      return (
        <Tooltip placement="bottom" text="Unlisted product">
          <Icon connotation="info" size="small" type="eye-hide-solid" />
        </Tooltip>
      );

    return null;
  };

  return (
    <Wrapper isLive={isLive} isPublic={isPublic}>
      <Card
        className="menu-lift-up mb-4 h-[380px]"
        data-testid={`product-card-${id}`}
        menu={
          useValidSession() && isProductOwner && canCreateProducts ? (
            <Menu
              data-testid="product-card-menu"
              icon="more-vertical-line"
              menuItems={PRODUCT_OPTIONS}
              onAction={onMenuOptionSelected}
            />
          ) : null
        }
        onClick={navigateToProductOverview}
      >
        {!isLive && lastVersion?.status ? (
          <Badge
            className="absolute -top-3 right-5"
            connotation={STATUS_TO_CONNOTATION_MAP[lastVersion.status]}
            data-testid={`status-version-${lastVersion.version}`}
            layout="soft"
          >
            {Text.toSentenceCase(lastVersion.status, true)}
          </Badge>
        ) : null}
        <Column className="h-full">
          <Row className="justify-between">
            <Column>
              <Avatar
                fallbackImage={`${
                  import.meta.env.VITE_PUBLIC_URL
                }/img/no-product-image.png`}
                size={AVATAR_SIZES.large}
                src={`${config.marketCDNUrl}/${icon}`}
                title={name}
              />
            </Column>

            {useValidSession() && isProductOwner && !isPublic && (
              <Column className="mt-3 mr-14 justify-start text-nowrap">
                <Row className="flex-nowrap">{getProductTypeIcon()}</Row>
              </Column>
            )}
          </Row>
          <Row className="mt-4 mb-8">
            <Column className="grow justify-between overflow-hidden">
              <H3 className="mt-1 mb-2 leading-8 wrap-break-word">
                {Text.truncateStringByWord(name, 50)}
              </H3>
              <Phrase className="text-primary-500 mb-2">{owner_name}</Phrase>
              <Phrase className="mt-2 line-clamp-3 text-sm">
                {short_description}
              </Phrase>
            </Column>
          </Row>
          <Row className="mt-auto">
            <Column className="grow">
              <Row className="flex-wrap">
                {tags.map((tag: string) => (
                  <Badge className="mt-1 mr-1" key={tag} layout="soft">
                    {tag}
                  </Badge>
                ))}
              </Row>
            </Column>
          </Row>
        </Column>
      </Card>
    </Wrapper>
  );
};
